import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Money } from "@/components/Money";
import { EmptyChart, ReportCard } from "@/components/reports/ReportCard";
import { pctText } from "@/lib/ranges";

const SECTION = { cogs: ["Cost of sales", "#64748B"], opex: ["Operating expenses", "#0D9488"], other: ["Depreciation and finance cost", "#0F172A"] };
const kfmt = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`);

const Tip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
      <div className="font-semibold text-slate-800">{p.label}</div>
      <div className="num text-slate-700"><Money value={p.amount} /> · {pctText(p.share_pct)}</div>
    </div>
  );
};

export const ExpenseBreakdown = ({ data, params, disabled }) => {
  const rows = data.expense_breakdown;
  const empty = rows.every((r) => !r.amount);
  return (
    <ReportCard id="expense_breakdown" title="Expense Breakdown" description="All 13 cost categories: COGS components, operating expenses and other charges" params={params} disabled={disabled}>
      <div className="grid gap-8 xl:grid-cols-[1.4fr_1fr]">
        {empty ? <EmptyChart testid="expense-chart-empty" /> : (
          <div className="h-[420px]" data-testid="expense-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tickFormatter={kfmt} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="label" width={190} tick={{ fontSize: 11.5, fill: "#334155" }} axisLine={false} tickLine={false} />
                <Tooltip content={<Tip />} cursor={{ fill: "#F1F5F9" }} />
                <Bar dataKey="amount" radius={[0, 3, 3, 0]} barSize={16} isAnimationActive={false}>
                  {rows.map((r) => <Cell key={r.key} fill={SECTION[r.section][1]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <table className="w-full self-start text-sm" data-testid="expense-table">
          <thead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr className="border-b border-slate-200"><th className="py-2 text-left">Category</th><th className="py-2 text-right">Amount</th><th className="py-2 text-right">Share</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="row-hover border-b border-slate-100" data-testid={`expense-row-${r.key}`}>
                <td className="py-1.5"><span className="mr-2 inline-block h-2 w-2 rounded-sm" style={{ background: SECTION[r.section][1] }} />{r.label}</td>
                <td className="py-1.5 text-right"><Money value={r.amount} /></td>
                <td className="num py-1.5 text-right text-slate-500">{pctText(r.share_pct)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-900 font-semibold" data-testid="expense-total-row">
              <td className="py-2">Total expenses</td><td className="py-2 text-right"><Money value={data.totals.total_expenses} /></td>
              <td className="num py-2 text-right">{data.totals.total_expenses > 0 ? "100.0%" : "—"}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex gap-4 text-xs text-slate-500">
        {Object.values(SECTION).map(([l, c]) => <span key={l} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: c }} />{l}</span>)}
      </div>
    </ReportCard>
  );
};
